
import React from 'react';
import { clearGameState } from "@/components/game/GamePersistenceV2";
import { modeAwareStorage } from "@/lib/managers/ModeAwareStorageManager";
import { toast } from "sonner";
import * as CookieManager from "@/lib/managers/CookieManager";
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import { WordSelectionStrategyFactory } from "./wordSelection/StrategyFactory";

/**
 * Centralized function to reset the game state without requiring a page reload
 * @param preserveMode If true, will preserve the current game mode
 * @returns Promise that resolves when the reset is complete
 */
export const resetGame = async (preserveMode = true): Promise<void> => {
  try {
    console.log('🎮 Starting game reset operation', {
      preserveMode,
      timestamp: new Date().toISOString()
    });
    
    // Get current game mode before reset if we want to preserve it
    const currentMode = preserveMode ? localStorage.getItem('babblegram_game_mode') : null;
    console.log('🎮 Game reset - current mode:', currentMode);
    
    // Check if this is a mode switch operation
    const isModeSwitch = localStorage.getItem('babblegram_mode_switch_in_progress') === 'true';
    console.log('🎮 Game reset - is mode switch:', isModeSwitch);
    
    // Signal that we're forcing a game reset
    localStorage.setItem('babblegram_force_game_reset', 'true');
    
    // Reset the word selection strategy only if we're in practice mode AND playing again
    if (currentMode === 'practice') {
      // For practice mode, explicitly set play again flag to force new sequence generation
      localStorage.setItem('babblegram_practice_mode_play_again', 'true');
      console.log('Practice mode detected during reset, setting play again flag', {
        timestamp: new Date().toISOString()
      });
      
      // Reset the strategy to force new sequence generation
      WordSelectionStrategyFactory.resetStrategy(currentMode);
    } else if (currentMode) {
      // For other modes like daily, reset the strategy immediately
      WordSelectionStrategyFactory.resetStrategy(currentMode);
    }
    
    // Batch localStorage operations for better performance
    const localStorageOperations = {
      'babblegram_practice_completion_state': null,
      [`babblegram_${currentMode || 'practice'}_completion_state`]: null,
      [`babblegram_${currentMode || 'practice'}_game_status`]: null,
      'babblegram_end_of_day': null
    };
    
    // Execute localStorage operations in batch
    Object.entries(localStorageOperations).forEach(([key, value]) => {
      if (value === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
      }
    });
    
    // CRITICAL FIX: Only clear completion state if NOT doing a mode switch
    // AND only if we're explicitly resetting the same mode (not switching between modes)
    if (!isModeSwitch) {
      // Use mode-aware storage for clearing completion state
      console.log('Clearing completion state during reset using mode-aware storage');
      modeAwareStorage.setCookie('game_completion_state', 'false');
      modeAwareStorage.setCookie('game_status', 'playing');
    } else {
      console.log('Skipping completion state clearing during mode switch to preserve completion status for all modes');
    }
    
    // Force a new practice sequence ONLY if explicitly playing again in practice mode
    if (currentMode === 'practice') {
      console.log('Practice mode: forcing new sequence generation', {
        timestamp: new Date().toISOString()
      });
    }
    
    // Set a timestamp for the strategy reset to force new word selection
    localStorage.setItem('babblegram_strategy_reset', Date.now().toString());
    
    // Clear state using new simplified system
    console.log('🔄 Clearing game state');
    try {
      await clearGameState();
      // Only clear current mode state, not cross-mode keys
      console.log('✅ Game state cleared successfully');
    } catch (error) {
      console.error('⚠️ Error clearing game state:', error);
    }
    
    // Restore the game mode if needed
    if (preserveMode && currentMode) {
      localStorage.setItem('babblegram_game_mode', currentMode);
      console.log('Restored game mode after reset:', currentMode);
    }
    
    // Dispatch a custom event to notify components about the reset
    window.dispatchEvent(new CustomEvent('babblegram_game_reset', { 
      detail: { timestamp: Date.now() } 
    }));
    
    console.log('✅ Game reset operation completed successfully');
    return Promise.resolve();
  } catch (error) {
    console.error('❌ Error resetting game:', error);
    console.error('❌ Error stack:', error instanceof Error ? error.stack : 'No stack trace available');
    toast.error("Failed to reset game state. Please try refreshing the page.");
    return Promise.reject(error);
  }
};

/**
 * Reset listener hook for components to respond to game resets
 * @param callback Function to call when a game reset happens
 */
export const useGameResetListener = (callback: () => void) => {
  React.useEffect(() => {
    const handleReset = () => {
      console.log('Game reset event detected, calling callback');
      callback();
    };
    
    window.addEventListener('babblegram_game_reset', handleReset);
    return () => {
      window.removeEventListener('babblegram_game_reset', handleReset);
    };
  }, [callback]);
};
